import React, { useRef, useState, useCallback } from "react";
import { QrReader } from "react-qr-reader";
import { Modal, Spinner } from "react-bootstrap";
import jsQR from "jsqr";

const MeetingQRScanner = ({ show, onJoin, onClose }) => {
  const [scanned, setScanned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("camera");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const fileRef = useRef();

  const handleQRImageFile = useCallback(
    (file) => {
      setUploadError("");
      setUploadSuccess("");
      if (!file || !file.type.startsWith("image/")) {
        setUploadError("Please upload a valid image file (PNG, JPG, etc.).");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          if (code?.data) {
            setUploadSuccess("QR detected! Joining meeting...");
            setScanned(true);
            onJoin(code.data);
          } else {
            setUploadError("No QR code found in this image. Please try another.");
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    [onJoin]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleQRImageFile(e.dataTransfer.files?.[0]);
    },
    [handleQRImageFile]
  );

  return (
    <>
      <style>{`
        /* ── Fix Bootstrap's inline padding-left that breaks centering ── */
        body.modal-open {
          padding-left: 0 !important;
          padding-right: 0 !important;
          overflow: hidden !important;
        }

        /* ── Dialog: centered, full-width safe, no side overflow ── */
        .qrm-modal.modal {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        .qrm-modal .modal-dialog {
          margin: auto !important;
          width: calc(100% - 24px) !important;
          max-width: 460px !important;
          padding: 0 !important;
          display: flex;
          align-items: center;
          min-height: calc(100% - 10px);
        }

        .qrm-modal .modal-content {
          border-radius: 18px;
          border: none;
          overflow: hidden;
          box-shadow: 0 24px 64px rgba(0,0,0,0.20);
          width: 100%;
          box-sizing: border-box;
        }

        .qrm-modal .modal-header {
          border: none;
          padding: 16px 16px 6px 16px;
          position: relative;
        }

        .qrm-modal .modal-title {
          font-size: clamp(15px, 4vw, 19px);
          font-weight: 700;
          width: 100%;
          text-align: center;
        }

        .qrm-modal .modal-body {
          padding: 12px 14px 18px 14px;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* ── Tabs ── */
        .qrm-tabs {
          display: flex;
          background: #f1f4fb;
          border-radius: 11px;
          padding: 4px;
          gap: 4px;
          margin-bottom: 14px;
          width: 100%;
          box-sizing: border-box;
        }

        .qrm-tab {
          flex: 1;
          min-width: 0;
          padding: 8px 4px;
          border: none;
          background: transparent;
          border-radius: 8px;
          font-size: clamp(12px, 3vw, 13.5px);
          font-weight: 600;
          color: #7a8499;
          cursor: pointer;
          transition: all 0.18s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          white-space: nowrap;
        }

        .qrm-tab.active {
          background: #fff;
          color: #2563eb;
          box-shadow: 0 2px 8px rgba(37,99,235,0.12);
        }

        .qrm-tab svg { flex-shrink: 0; }

        /* ── Camera: square, fully contained ── */
        .qrm-hint {
          text-align: center;
          font-size: clamp(11px, 3vw, 13px);
          color: #64748b;
          margin-bottom: 10px;
        }

        .qrm-camera-wrap {
          width: 100%;
          max-width: 100%;
          // border-radius: 12px;
          overflow: hidden;
          aspect-ratio: 1 / 1;
          background: #000;
          box-sizing: border-box;
          position: relative;
        }

        /* Force QrReader internals to fill the box */
        .qrm-camera-wrap > div {
          width: 100% !important;
          height: 100% !important;
          padding-top: 0 !important; /* react-qr-reader adds padding-top */
        }

        .qrm-camera-wrap video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }

        /* ── Drop Zone ── */
        .qrm-drop {
          border: 2px dashed #c7d2fe;
          border-radius: 14px;
          background: #f8f9ff;
          padding: clamp(18px, 5vw, 40px) 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.18s;
          box-sizing: border-box;
          width: 100%;
        }

        .qrm-drop.drag {
          border-color: #2563eb;
          background: #eff3ff;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.10);
        }

        .qrm-drop-icon { font-size: clamp(28px, 8vw, 44px); display: block; margin-bottom: 10px; }
        .qrm-drop-title { font-size: clamp(13px, 3.5vw, 15px); font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .qrm-drop-sub   { font-size: clamp(11px, 2.8vw, 13px); color: #64748b; margin-bottom: 14px; }

        .qrm-upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          background: linear-gradient(135deg, #2563eb, #4f46e5);
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: clamp(12px, 3vw, 13.5px);
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 3px 10px rgba(37,99,235,0.22);
          transition: opacity 0.18s;
        }
        .qrm-upload-btn:hover { opacity: 0.88; }

        .qrm-formats { margin-top: 10px; font-size: clamp(10px, 2.5vw, 11.5px); color: #94a3b8; }

        /* ── Alerts ── */
        .qrm-alert {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 9px;
          font-size: clamp(11px, 2.8vw, 13px);
          font-weight: 500;
          margin-top: 12px;
          box-sizing: border-box;
          word-break: break-word;
        }
        .qrm-alert.error   { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }
        .qrm-alert.success { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }

        .qrm-loader { text-align:center; padding:28px 0; }
        .qrm-loader p { margin-top:10px; color:#64748b; font-size:13px; }
      `}</style>

      <Modal
        style={{ backdropFilter: "blur(4px)" }}
        show={show}
        onHide={onClose}
        centered
        className="qrm-modal isscannermodal"
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title className="colorset">Scan Meeting QR Code</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{height: activeTab === "camera" ? "70vh" : "auto"}}>
          {/* Tabs */}
          <div className="qrm-tabs">
            <button
              className={`qrm-tab ${activeTab === "camera" ? "active" : ""}`}
              onClick={() => setActiveTab("camera")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              Camera
            </button>
            <button
              className={`qrm-tab ${activeTab === "upload" ? "active" : ""}`}
              onClick={() => setActiveTab("upload")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload Image
            </button>
          </div>

          {/* Camera Tab */}
          {activeTab === "camera" && (
            <>
              <p className="qrm-hint">Align the QR code inside the camera view.</p>
              {loading && (
                <div className="qrm-loader">
                  <Spinner animation="border" variant="primary" />
                  <p>Starting camera...</p>
                </div>
              )}
              <div style={{ display: loading ? "none" : "block" }}>
                <div className="qrm-camera-wrap">
                  <QrReader
                    constraints={{ video: { facingMode: "environment" } }}
                    scanDelay={500}
                    onResult={(res) => {
                      if (loading) setLoading(false);
                      if (res?.text && !scanned) {
                        setScanned(true);
                        onJoin(res.text);
                      }
                    }}
                    containerStyle={{ width: "100%", height: "100%", paddingTop: 0 }}
                    videoContainerStyle={{ width: "100%", height: "100%", paddingTop: 0, position: "relative" }}
                    videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Upload Tab */}
          {activeTab === "upload" && (
            <>
              <div
                className={`qrm-drop ${isDragging ? "drag" : ""}`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => fileRef.current?.click()}
              >
                <span className="qrm-drop-icon">🔲</span>
                <div className="qrm-drop-title">
                  {isDragging ? "Release to scan QR" : "Drop your QR image here"}
                </div>
                <div className="qrm-drop-sub">or click to browse from your device</div>
                <button
                  className="qrm-upload-btn"
                  onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Choose File
                </button>
                <div className="qrm-formats">PNG · JPG · JPEG · WEBP</div>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleQRImageFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />

              {uploadError && (
                <div className="qrm-alert error">
                  <svg width="15" height="15" style={{ flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {uploadError}
                </div>
              )}

              {uploadSuccess && (
                <div className="qrm-alert success">
                  <svg width="15" height="15" style={{ flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  {uploadSuccess}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MeetingQRScanner;
